import React, { ChangeEvent, KeyboardEvent, ReactNode, useState } from 'react';

/* NEW – allow callers to pass an icon element */
export interface InputProps {
  type: 'password' | 'text' | 'number' | 'list' | 'color' | 'file' | 'percent' | 'textarea';
  label?: string;
  name?: string;
  value: string | number | null;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (value) => void;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  disableSelect?: boolean;
  fullWidth?: boolean;
  className?: string;
  wrapperDivClassName?: string;
  showValidation?: boolean;
  invalidMessage?: string;
  min?: number;
  maxLength?: number;
  autoFocus?: boolean;
  ariaLabel?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;

  /* NEW props ----------------------------------------------------------- */
  startIcon?: ReactNode; // icon before the text
  endIcon?: ReactNode; // icon after  the text
  clipboard?: boolean; // show copy to clipboard button
  clipboardValue?: string; // optional: different value to copy than what's displayed
}

const Input: React.FC<InputProps> = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  required = false,
  disabled = false,
  fullWidth = true,
  className = '',
  wrapperDivClassName = '',
  showValidation = true,
  invalidMessage = undefined,
  min = undefined,
  maxLength,
  autoFocus,
  ariaLabel,
  onKeyDown,

  /* NEW */
  startIcon,
  endIcon,
  clipboard = false,
  clipboardValue,
}) => {
  /* helper flags for CSS modifier classes */
  const withIcon = !!startIcon;
  const withEndIcon = !!endIcon;

  const [showCopyTooltip, setShowCopyTooltip] = useState(false);

  const copyToClipboard = () => {
    const textToCopy = String(clipboardValue || value || '');
    navigator.clipboard.writeText(textToCopy);

    setShowCopyTooltip(true);
    setTimeout(() => {
      setShowCopyTooltip(false);
    }, 1000);
  };

  return (
    <div className={`react-input ${wrapperDivClassName}`}>
      {label && (
        <label className={getLabelClasses()} htmlFor={name}>
          {label}
          {required && <span>*</span>}
        </label>
      )}

      {/* wrapper so icon & input can be positioned together */}
      <div className={`react-input__field-wrapper ${clipboard ? 'input-with-copy' : ''}`}>
        {/* start icon (if any) */}
        {withIcon && <span className="pdq-input__icon">{startIcon}</span>}

        <input
          onClick={(e) => e.stopPropagation()}
          type={type}
          name={name}
          value={value as any}
          onChange={onChange}
          onBlur={() => onBlur?.(value)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          maxLength={maxLength}
          placeholder={placeholder}
          required={required}
          min={type === 'percent' ? 0 : min}
          max={type === 'percent' ? 100 : undefined}
          disabled={disabled}
          className={getInputClasses(
            disabled,
            type,
            fullWidth,
            className,
            required,
            value,
            showValidation,
            withIcon,
            withEndIcon,
          )}
        />

        {/* end-of-field icon (if any) */}
        {withEndIcon && <span className="pdq-input__icon pdq-input__icon--end">{endIcon}</span>}

        {/* clipboard button (if enabled) */}
        {clipboard && (
          <div className="copy">
            <img src="assets/icons/copy-to-clipboard.svg" alt="Copy to clipboard" onClick={copyToClipboard} />
            {showCopyTooltip && (
              <div className="pdq-mat-tooltip mat-mdc-tooltip">
                <div className="mat-mdc-tooltip-surface">Copied!</div>
              </div>
            )}
          </div>
        )}
      </div>

      {required && showValidation && !value && (
        <label className="invalid-msg react-input__error-msg">*{label} is required</label>
      )}
      {invalidMessage && <label className="invalid-msg react-input__error-msg">{invalidMessage}</label>}
    </div>
  );
};

const getLabelClasses = () => {
  return ['pdq-input-label', 'pdq-input-label--form-field', 'react-input__label'].join(' ');
};

/* 2 NEW boolean params at the end */
const getInputClasses = (
  disabled,
  type,
  fullWidth,
  className,
  required,
  value,
  showValidation,
  withIcon,
  withEndIcon,
) => {
  return [
    'pdq-input',
    'pdq-input--default',
    disabled ? 'disabled' : 'enabled',
    fullWidth ? 'pdq-input--full-width' : '',
    'pdq-input-background--default',
    'pdq-input-border--true',
    'pdq-input--medium',
    type,
    className,
    withIcon ? 'with-icon' : '',
    withEndIcon ? 'with-end-icon' : '',
    required && showValidation && !value ? 'invalid' : '',
  ].join(' ');
};

export default Input;
